'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

interface Appointment {
  id: string;
  date: string;
  time: string;
  reason: string;
  status: string;
}

interface HealthSummary {
  latestSpo2: number | null;
  activeMeds: number;
  totalVisits: number;
}

export default function PatientPage() {
  const router = useRouter();
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [healthSummary, setHealthSummary] = useState<HealthSummary>({
    latestSpo2: null,
    activeMeds: 0,
    totalVisits: 0,
  });

  useEffect(() => {
    const init = async () => {
      try {
        const loggedIn = localStorage.getItem('isPatientLoggedIn');
        if (loggedIn !== 'true') {
          router.replace('/patient-login');
          return;
        }
        const storedPhone = localStorage.getItem('patientPhone') || '';
        setPhone(storedPhone);

        if (storedPhone) {
          await fetchPatientData(storedPhone);
        }
      } catch {
        router.replace('/patient-login');
        return;
      }
      setIsLoading(false);
    };
    init();
  }, [router]);

  const fetchPatientData = async (patientPhone: string) => {
    const supabase = createClient();
    if (!supabase) return;

    // Normalize phone: strip +91 prefix for DB lookup
    const normalizedPhone = patientPhone.replace(/^\+91/, '').replace(/\D/g, '');
    const phoneVariants = [
      normalizedPhone,
      `+91${normalizedPhone}`,
      patientPhone,
    ];

    try {
      // Fetch upcoming appointments (match by any phone variant)
      const { data: apptData } = await supabase
        .from('appointments')
        .select('id, date, time, reason, status')
        .in('phone', phoneVariants)
        .order('created_at', { ascending: false })
        .limit(5);

      if (apptData) {
        setAppointments(apptData);
      }

      // Fetch active prescriptions count
      const { data: medsData } = await supabase
        .from('prescriptions')
        .select('id')
        .in('patient_phone', phoneVariants)
        .eq('is_active', true);

      // Fetch total visit count
      const { data: visitsData } = await supabase
        .from('visit_history')
        .select('id, spo2')
        .in('patient_phone', phoneVariants)
        .order('created_at', { ascending: false });

      // Fetch latest vitals for SpO2
      const { data: vitalsData } = await supabase
        .from('vitals')
        .select('spo2')
        .in('patient_phone', phoneVariants)
        .order('created_at', { ascending: false })
        .limit(1);

      const latestSpo2 =
        vitalsData?.[0]?.spo2 ??
        visitsData?.[0]?.spo2 ??
        null;

      setHealthSummary({
        latestSpo2,
        activeMeds: medsData?.length ?? 0,
        totalVisits: visitsData?.length ?? 0,
      });
    } catch {
      // Non-critical — show empty state
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('isPatientLoggedIn');
      localStorage.removeItem('patientPhone');
    } catch {}
    router.replace('/patient-login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const displayPhone = phone ? `+91 ${phone}` : 'Patient';

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getStatusLabel = (status: string) => {
    if (!status) return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-teal-600 text-white px-4 py-4 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 40 40" className="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="14" cy="20" rx="7" ry="11" opacity="0.9" />
              <ellipse cx="26" cy="20" rx="7" ry="11" opacity="0.9" />
              <rect x="18" y="9" width="4" height="22" rx="2" fill="white" opacity="0.6" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold leading-tight">Patient Portal</p>
            <p className="text-xs text-teal-100">Dr. Neeraj Lung Care</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 transition-colors text-white text-xs font-semibold px-3 py-2 rounded-lg"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </button>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Welcome Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-7 h-7 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Welcome back 👋</p>
              <h1 className="text-lg font-bold text-gray-900 mt-0.5">Patient</h1>
              <p className="text-sm text-teal-600 font-semibold mt-0.5">{displayPhone}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 bg-teal-50 text-teal-700 text-xs font-semibold px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
              Active Patient
            </span>
            <span className="text-xs text-gray-400">Centre for Lung Care and Chest Diseases, Jammu</span>
          </div>
        </div>

        {/* Health Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 text-center">
            <p className="text-xl font-bold text-gray-900">
              {healthSummary.latestSpo2 !== null ? `${healthSummary.latestSpo2}%` : '—'}
            </p>
            <p className="text-[10px] text-gray-400 font-medium mt-0.5">SpO₂</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 text-center">
            <p className="text-xl font-bold text-gray-900">{healthSummary.activeMeds}</p>
            <p className="text-[10px] text-gray-400 font-medium mt-0.5">Medications</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 text-center">
            <p className="text-xl font-bold text-gray-900">{healthSummary.totalVisits}</p>
            <p className="text-[10px] text-gray-400 font-medium mt-0.5">Total Visits</p>
          </div>
        </div>

        {/* Appointments */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-bold text-gray-900">My Appointments</h2>
            <span className="text-xs text-teal-600 font-semibold">
              {appointments.length > 0 ? `${appointments.length} found` : 'None yet'}
            </span>
          </div>
          {appointments.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <svg className="w-10 h-10 text-gray-200 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-gray-400">No appointments found</p>
              <p className="text-xs text-gray-300 mt-1">Book your first appointment below</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {appointments.map((appt) => (
                <div key={appt.id} className="px-5 py-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{appt.date} · {appt.time}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{appt.reason || 'Consultation'}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${getStatusColor(appt.status)}`}>
                    {getStatusLabel(appt.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Link
            href="/patient/book-appointment"
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 hover:border-teal-200 hover:shadow-md transition-all"
          >
            <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-gray-800">Book Appointment</span>
          </Link>
          <Link
            href="/patient/my-health"
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 hover:border-teal-200 hover:shadow-md transition-all"
          >
            <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-gray-800">My Health</span>
          </Link>
          <Link
            href="/patient/lung-academy"
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 hover:border-teal-200 hover:shadow-md transition-all"
          >
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-gray-800">Lung Academy</span>
          </Link>
          <Link
            href="/patient/ask-question"
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 hover:border-teal-200 hover:shadow-md transition-all"
          >
            <div className="w-9 h-9 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-gray-800">Ask a Question</span>
          </Link>
        </div>

        {/* Back to website */}
        <div className="text-center pb-4">
          <a href="/" className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
            ← Back to Centre for Lung Care and Chest Diseases website
          </a>
        </div>
      </div>
    </div>
  );
}
